import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import './games/games.css'

// ── TitleCard — the ONE loading screen ───────────────────────────────────────
// The app used to have three ways of waiting: the boot splash (word + film strip), the
// games' title card (glyph, eyebrow, serif title, promise, film strip) and an old
// spinning film reel on every other lazy route and page (Jason, 2026-10-25: "a couple
// of variations… let's make sure they are all the same"). This is the games' card,
// generalized: every route and every page-level wait opens on it, the boot splash in
// index.html mirrors it by hand, and the film-strip beam runs at one unhurried speed.
// It owns the screen while up (no coach, ask or toast may land on it) and is portaled
// so a transformed wrapper can never scope it.
export function TitleCard({ tone = 'var(--amber)', glyph, eyebrow = 'NextEp', title, line, caption = 'Loading…', hold = true }) {
  useEffect(() => { if (!hold) return; window.__nextepModalHold = true; return () => { window.__nextepModalHold = false } }, [hold])
  return createPortal(
    <div className="gm-stage" style={{ '--gm-tone': tone }} role="status" aria-live="polite" aria-label={caption}>
      <div className="gm-glow a" /><div className="gm-glow b" />
      <div className="gm-glyph-in" style={{ width: 72, height: 72, borderRadius: 20, background: tone, color: 'var(--amber-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 16px 40px -12px color-mix(in srgb, ${tone} 70%, transparent)` }}>
        {glyph || <Mark />}
      </div>
      <div className="gm-eyebrow gm-rise" style={{ '--d': '160ms', color: tone, marginTop: 22 }}>{eyebrow}</div>
      {title && <div className="gm-rise" style={{ '--d': '240ms', fontFamily: 'var(--font-serif)', fontSize: 36, fontWeight: 500, letterSpacing: '-0.01em', color: 'var(--text-strong)', marginTop: 6, lineHeight: 1, textAlign: 'center' }}>{title}</div>}
      {line && <div className="gm-rise" style={{ '--d': '340ms', color: 'var(--text-muted)', fontSize: 14, marginTop: 10, textAlign: 'center', maxWidth: 260, lineHeight: 1.45 }}>{line}</div>}
      <FilmStrip className="gm-rise" style={{ '--d': '480ms', marginTop: 28 }} />
      <div className="gm-rise" style={{ '--d': '560ms', color: 'var(--text-dim)', fontSize: 12.5, marginTop: 12 }}>{caption}</div>
    </div>,
    document.body,
  )
}

// The film strip with its beam, on its own: the inline wait for a section that is
// still arriving (an import, a room, an invite). Same strip, same speed.
export function FilmStrip({ className = '', style, tone }) {
  return <div className={`gm-film ${className}`} style={{ ...(tone ? { '--gm-tone': tone } : {}), ...style }} aria-hidden="true"><div className="beam" /></div>
}

// The chevron mark, the app's own glyph.
function Mark() {
  return <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true"><g fill="currentColor"><polygon points="5,6.3 7.1,6.3 11.7,12 7.1,17.7 5,17.7 9.6,12"/><polygon points="11.4,6.3 13.5,6.3 18.1,12 13.5,17.7 11.4,17.7 16,12"/><rect x="19.6" y="6.3" width="2.8" height="11.4" rx="1.4"/></g></svg>
}

// What each route's card says. One place, so a new page gets a card by adding a line.
const ROUTE_CARDS = [
  [/^\/$/,              { title: 'Home',          line: 'Your next episode, on one card.', caption: 'Dealing the deck…' }],
  [/^\/list/,           { title: 'My List',       line: 'Every show, and where you are in it.', caption: 'Opening your list…' }],
  [/^\/tonight/,        { title: 'Tonight',       line: 'What you’re watching tonight.', caption: 'Setting the scene…' }],
  [/^\/show\//,         { title: 'The show',      line: 'Episodes, where to watch, what’s next.', caption: 'Opening the show…' }],
  [/^\/profile/,        { title: 'Profile',       line: 'Your run, your cards, your stats.', caption: 'Opening your profile…' }],
  [/^\/settings/,       { title: 'Settings',      line: 'Every switch, in one place.', caption: 'Opening settings…' }],
  [/^\/notifications/,  { title: 'Notifications', line: 'Shows, people, and the games.', caption: 'Checking the inbox…' }],
  [/^\/discover/,       { title: 'Discover',      line: 'What’s trending and what’s airing.', caption: 'Looking around…' }],
  [/^\/search/,         { title: 'Search',        line: 'Any show on television.', caption: 'Opening search…' }],
  [/^\/history/,        { title: 'History',       line: 'Everything you’ve marked.', caption: 'Rolling back the tape…' }],
  [/^\/stats/,          { title: 'Stats',         line: 'Your TV, in numbers.', caption: 'Adding it up…' }],
  [/^\/aftershow/,      { title: 'The Aftershow', line: 'Spoiler-safe episode talk.', caption: 'Opening the room…' }],
  [/^\/help/,           { title: 'Help',          line: 'How NextEp works.', caption: 'Opening help…' }],
  [/^\/import|^\/sync/, { title: 'Import',        line: 'Bring your whole history over.', caption: 'Opening the importer…' }],
]
export function cardForPath(pathname = '') {
  const hit = ROUTE_CARDS.find(([re]) => re.test(pathname))
  return hit ? hit[1] : { title: null, line: null, caption: 'Loading…' }
}
